import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useApi } from '../../api/api';
import { useEditModeLookups } from '../../contexts/EditModeLookupsContext';
import { slugToLabel } from '../../pages/registry/componentRegistry';
import './TopPanelEditControls.css';

/**
 * Edit controls for the top panel. Each module/step has its own list of components.
 * Components are initially auto-populated from getPageComponentSlugs API.
 * User selects a module, adds/removes components via dropdown and delete buttons, then saves.
 * Uses updatePanelLayout with page_type='step', panel_slug='top-panel', all components in 'no-section'.
 */
export default function TopPanelEditControls({
  modules,
  selectedModuleId,
  componentSlugsByModule,
  onComponentSlugsChange,
}) {
  const { getPageComponentSlugs, updatePanelLayout } = useApi();
  const lookups = useEditModeLookups();
  const [loading, setLoading] = useState(false);
  const [saveStatus, setSaveStatus] = useState(null); // 'saving' | 'saved' | null
  const [saveError, setSaveError] = useState(null);

  const allComponentSlugs = useMemo(
    () => (lookups?.panelComponents?.length ? lookups.panelComponents.map((c) => c.slug) : []),
    [lookups?.panelComponents]
  );

  // Fetch component slugs when a module is selected and we don't have them yet
  useEffect(() => {
    if (!selectedModuleId || !getPageComponentSlugs) return;
    const cached = componentSlugsByModule?.[selectedModuleId];
    if (cached !== undefined) return; // Already have data

    setLoading(true);
    getPageComponentSlugs(selectedModuleId)
      .then((response) => {
        const slugs = response?.data ?? [];
        if (Array.isArray(slugs) && slugs.length > 0) {
          onComponentSlugsChange(selectedModuleId, slugs);
        } else {
          onComponentSlugsChange(selectedModuleId, []);
        }
      })
      .catch((err) => {
        console.error('Error fetching component slugs:', err);
        onComponentSlugsChange(selectedModuleId, []);
      })
      .finally(() => setLoading(false));
  }, [selectedModuleId, getPageComponentSlugs, componentSlugsByModule, onComponentSlugsChange]);

  const selectedSlugs = componentSlugsByModule?.[selectedModuleId] ?? [];
  const selectedModule = modules?.find((m) => m.id === selectedModuleId);

  const handleRemoveSlug = useCallback(
    (slug) => {
      if (!selectedModuleId) return;
      const current = componentSlugsByModule?.[selectedModuleId] ?? [];
      onComponentSlugsChange(selectedModuleId, current.filter((s) => s !== slug));
    },
    [selectedModuleId, componentSlugsByModule, onComponentSlugsChange]
  );

  const handleAddComponent = useCallback(
    (e) => {
      const slug = e.target.value;
      if (!slug || !selectedModuleId) return;
      e.target.value = '';
      const current = componentSlugsByModule?.[selectedModuleId] ?? [];
      if (current.includes(slug)) return;
      onComponentSlugsChange(selectedModuleId, [...current, slug].sort());
    },
    [selectedModuleId, componentSlugsByModule, onComponentSlugsChange]
  );

  const handleSave = useCallback(async () => {
    if (!updatePanelLayout || !selectedModuleId) return;
    const selectedModule = modules?.find((m) => m.id === selectedModuleId);
    const resourceId = selectedModule?.resource_id;
    if (resourceId == null) {
      setSaveError('No resource ID for this step');
      return;
    }
    setSaveStatus('saving');
    setSaveError(null);
    try {
      const slugs = componentSlugsByModule?.[selectedModuleId] ?? [];
      const body = {
        sections: [
          {
            section_slug: 'no-section',
            section_order: 0,
            components: slugs.map((slug, i) => ({ component_slug: slug, component_order: i })),
          },
        ],
      };
      await updatePanelLayout('step', resourceId, 'top-panel', body);
      setSaveStatus('saved');
      setTimeout(() => setSaveStatus(null), 2000);
    } catch (err) {
      const msg = err?.response?.data?.detail ?? err?.message ?? 'Failed to save';
      setSaveError(typeof msg === 'string' ? msg : JSON.stringify(msg));
      setSaveStatus(null);
    }
  }, [updatePanelLayout, selectedModuleId, modules, componentSlugsByModule]);

  const availableToAdd = allComponentSlugs.filter((s) => !selectedSlugs.includes(s));

  if (!modules?.length) return null;

  return (
    <div className="top-panel-edit-controls">
      <h3 className="top-panel-edit-title">Edit Top Panel</h3>
      <p className="top-panel-edit-subtitle">
        Select a step above, then add or remove components to customize what appears for that step.
      </p>

      {!selectedModuleId ? (
        <div className="top-panel-edit-empty">Select a step to edit its components</div>
      ) : (
        <div className="top-panel-edit-section">
          <div className="top-panel-edit-section-header">
            <span className="top-panel-edit-section-title">
              Components for &quot;{selectedModule?.name ?? selectedModuleId}&quot;
            </span>
            {loading && <span className="top-panel-edit-loading">Loading…</span>}
          </div>

          <div className="top-panel-edit-component-list">
            {selectedSlugs.length === 0 && !loading ? (
              <div className="top-panel-edit-empty">No components yet. Add one below.</div>
            ) : (
              selectedSlugs.map((slug) => {
                const comp = lookups?.panelComponents?.find((c) => c.slug === slug);
                const label = comp?.title ?? slugToLabel(slug);
                return (
                  <div key={slug} className="top-panel-edit-component-row">
                    <span className="top-panel-edit-component-label">{label} ({slug})</span>
                    <button
                      type="button"
                      className="top-panel-edit-remove-btn"
                      onClick={() => handleRemoveSlug(slug)}
                      title="Remove"
                    >
                      ✕
                    </button>
                  </div>
                );
              })
            )}
          </div>

          {availableToAdd.length > 0 && (
            <div className="top-panel-edit-add">
              <select
                className="top-panel-edit-add-select"
                value=""
                onChange={handleAddComponent}
              >
                <option value="">+ Add component</option>
                {availableToAdd.map((slug) => {
                  const comp = lookups?.panelComponents?.find((c) => c.slug === slug);
                  const label = comp?.title ?? slugToLabel(slug);
                  return (
                    <option key={slug} value={slug}>
                      {label} ({slug})
                    </option>
                  );
                })}
              </select>
            </div>
          )}

          {saveError && <div className="top-panel-edit-error">{saveError}</div>}
          <button
            type="button"
            className="top-panel-edit-save-btn"
            onClick={handleSave}
            disabled={loading || saveStatus === 'saving' || selectedModule?.resource_id == null}
          >
            {saveStatus === 'saving' ? 'Saving…' : saveStatus === 'saved' ? 'Saved' : 'Save'}
          </button>
        </div>
      )}
    </div>
  );
}
